import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { paymentAPI } from '../services/api';
import { toast } from 'sonner';
import { Check, Loader } from 'lucide-react';

const Subscription = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    try {
      const response = await paymentAPI.getMySubscription();
      setCurrentSubscription(response.data);
    } catch (error) {
      console.log('No active subscription');
    } finally {
      setChecking(false);
    }
  };

  const handleSubscribe = async (packageId) => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await paymentAPI.createCheckout({
        package_id: packageId,
        origin_url: originUrl
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Failed to start subscription process');
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12 fade-in">
          <h1 className="text-4xl font-bold mb-4">Choose Your Plan</h1>
          <p className="text-lg text-gray-600">
            One subscription covers participation as a Core member on unlimited teams.
          </p>
        </div>

        {currentSubscription && (
          <Card className="glass-effect p-6 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold">Current Subscription</h3>
                <p className="text-gray-600">
                  {currentSubscription.subscription_type} - ${currentSubscription.amount}/
                  {currentSubscription.subscription_type === 'monthly' ? 'month' : 'year'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Expires: {new Date(currentSubscription.current_period_end).toLocaleDateString()}
                </p>
              </div>
              <span className="status-badge status-active">Active</span>
            </div>
          </Card>
        )}

        <div className="grid md:grid-cols-2 gap-8">
          {/* Monthly Plan */}
          <Card className="glass-effect p-8 relative">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold mb-2">Monthly</h3>
              <div className="mb-4">
                <span className="text-5xl font-bold">$9.99</span>
                <span className="text-gray-600">/month</span>
              </div>
            </div>
            
            <ul className="space-y-4 mb-8">
              <Feature text="Unlimited teams as Core member" />
              <Feature text="All features included" />
              <Feature text="Priority support" />
              <Feature text="Cancel anytime" />
            </ul>
            
            <Button
              className="w-full bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600"
              onClick={() => handleSubscribe('subscription_monthly')}
              disabled={loading || currentSubscription?.subscription_type === 'monthly'}
              data-testid="subscribe-monthly-btn"
            >
              {loading ? 'Processing...' : currentSubscription?.subscription_type === 'monthly' ? 'Current Plan' : 'Subscribe Monthly'}
            </Button>
          </Card>

          {/* Annual Plan */}
          <Card className="glass-effect p-8 relative border-2 border-blue-500">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-600 to-green-500 text-white px-4 py-1 rounded-full text-sm font-medium">
              Save 17%
            </div>
            
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold mb-2">Annual</h3>
              <div className="mb-4">
                <span className="text-5xl font-bold">$99.99</span>
                <span className="text-gray-600">/year</span>
              </div>
              <p className="text-sm text-gray-600">That's $8.33/month</p>
            </div>
            
            <ul className="space-y-4 mb-8">
              <Feature text="Unlimited teams as Core member" />
              <Feature text="All features included" />
              <Feature text="Priority support" />
              <Feature text="Best value" />
            </ul>
            
            <Button
              className="w-full bg-gradient-to-r from-blue-600 to-green-500 hover:from-blue-700 hover:to-green-600"
              onClick={() => handleSubscribe('subscription_annual')}
              disabled={loading || currentSubscription?.subscription_type === 'annual'}
              data-testid="subscribe-annual-btn"
            >
              {loading ? 'Processing...' : currentSubscription?.subscription_type === 'annual' ? 'Current Plan' : 'Subscribe Annually'}
            </Button>
          </Card>
        </div>

        <div className="mt-12 text-center text-sm text-gray-600">
          <p>All plans include access to all features.</p>
          <p className="mt-2">Support team members participate free - no subscription required.</p>
        </div>
      </div>
    </div>
  );
};

const Feature = ({ text }) => (
  <li className="flex items-center gap-3">
    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
      <Check className="w-4 h-4 text-green-600" />
    </div>
    <span className="text-gray-700">{text}</span>
  </li>
);

export default Subscription;